import React, { useEffect, useState } from "react";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsivePie } from "@nivo/pie";
import axios from "axios";

export default function UserActivityStatus() {
  const [username, setUsername] = useState("");
  const [barData, setBarData] = useState([]);
  const [pieData, setPieData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchData = async (searchUsername = "") => {
    setLoading(true);
    setError("");

    try {
      const res = await axios.get(
        `http://localhost:5000/api/user-activity-status${
          searchUsername ? `?username=${searchUsername}` : ""
        }`
      );

      const data = res.data;

      if (!data || data.length === 0) {
        setError("No data available");
        setBarData([]);
        setPieData([]);
        setLoading(false);
        return;
      }

      // ✅ Filter clubs with at least 1 assigned activity
      const filtered = data.filter((row) => row.total_assigned >= 1);

      // ✅ Bar chart
      const barChart = filtered.map((row) => ({
        name: row.enr_club_name,
        total_assigned: row.total_assigned,
        total_submitted: row.total_submitted,
        pending: row.pending,
      }));
      setBarData(barChart);

      // ✅ Pie chart
      const pieChart = filtered.map((row) => ({
        id: row.enr_club_name,
        label: row.enr_club_name,
        value: row.pending,
      }));
      setPieData(pieChart);
    } catch (err) {
      console.error("Error fetching user activity data:", err);
      setError("Error fetching data. Check console.");
      setBarData([]);
      setPieData([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fetch all users initially
  useEffect(() => {
    fetchData();
  }, []);

  const handleSearch = () => {
    if (!username.trim()) {
      fetchData(); // fetch all if empty
    } else {
      fetchData(username.trim());
    }
  };

  return (
    <div style={{ padding: "40px", minHeight: "100vh", background: "#f4f6f9" }}>
      <h1
        style={{
          textAlign: "center",
          marginBottom: "40px",
          fontSize: "2rem",
          fontWeight: "700",
          color: "#2c3e50",
        }}
      >
        📊 User Activity Status
      </h1>

      {/* 🔍 Search Input */}
      <div style={{ marginBottom: "40px", textAlign: "center" }}>
        <input
          type="text"
          placeholder="Search by username..."
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          style={{
            padding: "12px 18px",
            width: "300px",
            marginRight: "12px",
            border: "1px solid #ccc",
            borderRadius: "25px",
            outline: "none",
            fontSize: "14px",
          }}
        />
        <button
          onClick={handleSearch}
          style={{
            padding: "12px 28px",
            backgroundColor: "#36A2EB",
            color: "#fff",
            fontWeight: "600",
            border: "none",
            borderRadius: "25px",
            cursor: "pointer",
            transition: "0.3s ease",
          }}
          onMouseOver={(e) => (e.target.style.backgroundColor = "#1d8adb")}
          onMouseOut={(e) => (e.target.style.backgroundColor = "#36A2EB")}
        >
          Search
        </button>
      </div>

      {loading && <p style={{ textAlign: "center" }}>⏳ Loading...</p>}
      {error && <p style={{ color: "red", textAlign: "center" }}>{error}</p>}

      {/* 📉 Bar Chart */}
      {barData.length > 0 && (
        <div
          style={{
            height: 500,
            marginBottom: "60px",
            background: "#fff",
            padding: "25px",
            borderRadius: "16px",
            boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
          }}
        >
          <h3
            style={{
              textAlign: "center",
              marginBottom: "20px",
              fontSize: "1.2rem",
              color: "#34495e",
            }}
          >
            📉 Activity Status Overview
          </h3>
          <ResponsiveBar
            data={barData}
            keys={["total_assigned", "total_submitted", "pending"]}
            indexBy="name"
            margin={{ top: 50, right: 150, bottom: 80, left: 80 }}
            padding={0.4}
            axisBottom={{
              tickRotation: -30,
              legend: "Club Name",
              legendPosition: "middle",
              legendOffset: 50,
            }}
            axisLeft={{
              legend: "Activity Count",
              legendPosition: "middle",
              legendOffset: -60,
            }}
            colors={["#FF6384", "#36A2EB", "#FFCE56"]}
            groupMode="grouped"
            labelSkipWidth={12}
            labelSkipHeight={12}
            labelTextColor="#fff"
            legends={[
              {
                dataFrom: "keys",
                anchor: "bottom-right",
                direction: "column",
                justify: false,
                translateX: 120,
                itemsSpacing: 5,
                itemWidth: 100,
                itemHeight: 20,
                itemTextColor: "#555",
                symbolSize: 20,
              },
            ]}
            tooltip={({ id, value, indexValue }) => (
              <div
                style={{
                  padding: "6px 10px",
                  background: "white",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                }}
              >
                <strong>
                  {indexValue} • {id}: {value}
                </strong>
              </div>
            )}
          />
        </div>
      )}

      {/* 🥧 Pie Chart */}
      {pieData.length > 0 && (
        <div
          style={{
            height: 500,
            background: "#fff",
            padding: "25px",
            borderRadius: "16px",
            boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
          }}
        >
          <h3
            style={{
              textAlign: "center",
              marginBottom: "20px",
              fontSize: "1.2rem",
              color: "#34495e",
            }}
          >
            🥧 Pending Activities Distribution
          </h3>
          <ResponsivePie
            data={pieData}
            margin={{ top: 50, right: 150, bottom: 80, left: 150 }}
            innerRadius={0.45}
            padAngle={1.2}
            cornerRadius={4}
            activeOuterRadiusOffset={8}
            colors={{ scheme: "set2" }}
            borderWidth={1}
            borderColor={{ from: "color", modifiers: [["darker", 0.3]] }}
            arcLinkLabelsSkipAngle={10}
            arcLinkLabelsTextColor="#333"
            arcLinkLabelsThickness={2}
            arcLinkLabelsColor={{ from: "color" }}
            arcLabelsSkipAngle={10}
            arcLabelsTextColor={{ from: "color", modifiers: [["darker", 2]] }}
            legends={[
              {
                anchor: "bottom",
                direction: "row",
                justify: false,
                translateY: 50,
                itemsSpacing: 10,
                itemWidth: 100,
                itemHeight: 20,
                itemTextColor: "#555",
                symbolSize: 18,
              },
            ]}
          />
        </div>
      )}
    </div>
  );
}
